import React, { useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { useCurrency } from '../CurrencyContext';
import { useTheme } from '../ThemeContext';
import AddTransaction from './AddTransaction';
import Settings from './Settings';
import Defaults from './Defaults';
import TransactionModal from './TransactionModal';
import PasswordSetupModal from './PasswordSetupModal';
import FetchingIndicator from './FetchingIndicator';
import PendingUploadsIndicator from './PendingUploadsIndicator';
import api from '../api';
import { useQueryClient } from '@tanstack/react-query';
import { useAllTransactions, useAccounts, useConsolidatedAccounts, useProfileSettings, QUERY_KEYS, useBudgetSync, usePendingQueueProcessor } from '../TransactionService';
import MobileDashboard from './MobileDashboard';
import WidgetsSection from './WidgetsSection';
import './Dashboard.css';


// Anchor-day convention: every cycle starts ON the anchor day in the previous
// or current month (whichever is most recent and <= today) and ends the DAY
// BEFORE the next anchor. Matches how billing cycles work in the real world.
function getCycleDates(userDefaults) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const y = today.getFullYear();
  const m = today.getMonth();

  // The billing cycle can be configured from two places that historically
  // used different keys:
  //   • Settings  → `billing_cycle`      (month_last_friday | month_last_day | manual)
  //   • Defaults  → `billing_cycle_type` (last_friday | last_day | manual)
  // Read whichever is present and normalize to a single internal value so a
  // choice made in either modal is honored. Without this, picking "Last
  // Friday" in Defaults was silently ignored and the cycle fell back to the
  // calendar month (e.g. June starting on Jun 1 instead of May 29).
  const rawType = userDefaults.billing_cycle || userDefaults.billing_cycle_type || 'month_last_day';
  const cycle = (
    rawType === 'last_friday' ? 'month_last_friday' :
    rawType === 'last_day'    ? 'month_last_day' :
    rawType // 'manual' or already-normalized 'month_last_*'
  );
  const manualDay = parseInt(userDefaults.billing_manual_day || userDefaults.billing_cycle_day || 1, 10);

  // Resolve the anchor date in an arbitrary (year, month).
  const anchorDate = (yr, mo) => {
    if (cycle === 'month_last_day') {
      return new Date(yr, mo + 1, 0); // last day of month
    }
    if (cycle === 'month_last_friday') {
      const last = new Date(yr, mo + 1, 0);
      const diff = (last.getDay() + 7 - 5) % 7;
      last.setDate(last.getDate() - diff);
      return last;
    }
    if (cycle === 'manual') {
      const lastOfMonth = new Date(yr, mo + 1, 0).getDate();
      return new Date(yr, mo, Math.min(manualDay, lastOfMonth));
    }
    return new Date(yr, mo + 1, 0);
  };

  const currAnchor = anchorDate(y, m);
  let start, nextAnchor;
  if (today >= currAnchor) {
    start = currAnchor;
    nextAnchor = anchorDate(y, m + 1);
  } else {
    start = anchorDate(y, m - 1);
    nextAnchor = currAnchor;
  }
  const end = new Date(nextAnchor);
  end.setDate(end.getDate() - 1);
  return { from: fmtDate(start), to: fmtDate(end) };
}

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const CATEGORIES = [
  { id: 'rent', icon: '🏠', label: 'Rent' },
  { id: 'groceries', icon: '🛒', label: 'Groceries' },
  { id: 'utilities', icon: '⚡', label: 'Utilities' },
  { id: 'metro', icon: '🚇', label: 'Metro' },
  { id: 'health', icon: '💊', label: 'Health' },
  { id: 'dining', icon: '🍔', label: 'Dining Out' },
  { id: 'food', icon: '🍱', label: 'Food' },
  { id: 'shopping', icon: '🛍️', label: 'Shopping' },
  { id: 'entertainment', icon: '🎬', label: 'Entertainment' },
  { id: 'travel', icon: '✈️', label: 'Travel' },
  { id: 'savings', icon: '💰', label: 'Savings' },
  { id: 'investments', icon: '📈', label: 'Investments' },
  { id: 'emi', icon: '🏦', label: 'EMI / Loan' },
  { id: 'other', icon: '📦', label: 'Other' },
];

const NAV_TABS = ['dashboard', 'all_transactions', 'widgets', 'splitgroups'];

// ── Floating bottom nav — mobile only ──
const FloatingNav = ({ activeTab, setActiveTab }) => {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  if (!isMobile) return null;

  const tabs = [
    { id: 'dashboard',        icon: '🏠', label: 'Home'    },
    { id: 'all_transactions', icon: '📋', label: 'Txns'    },
    { id: 'widgets',          icon: '📊', label: 'Insights' },
    { id: 'splitgroups',      icon: '👥', label: 'Groups'  },
  ];

  return (
    <div className="db-floating-nav">
      {tabs.map(tab => (
        <button
          key={tab.id}
          className={`db-floating-nav-item${activeTab === tab.id ? ' active' : ''}`}
          onClick={() => setActiveTab(tab.id)}
        >
          <span className="db-floating-nav-icon">{tab.icon}</span>
          {activeTab === tab.id && <span className="db-floating-nav-label">{tab.label}</span>}
        </button>
      ))}
    </div>
  );
};

const formatAmount = (num) => {
  const abs = Math.abs(num);
  return abs.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const formatDateGroup = (dateStr) => {
  const d = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00');
  d.setHours(0, 0, 0, 0);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const t = d.getTime();
  if (t === today.getTime()) return 'Today';
  if (t === yesterday.getTime()) return 'Yesterday';
  const opts = { weekday: 'long', day: 'numeric', month: 'long' };
  if (d.getFullYear() !== today.getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-GB', opts);
};



const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const { currency } = useCurrency();
  const { theme, toggleTheme } = useTheme();
  const sym = currency.symbol;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [resetKey, setResetKey] = useState(0);
  const [mobileDashMode, setMobileDashMode] = useState(null);
  const [showFullHeroStats, setShowFullHeroStats] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const queryClient = useQueryClient();

  // Hydrate dashboards / selection / settings synchronously from localStorage
  // so the page renders with cached IDs *immediately*, even when the backend
  // is still waking up. Without this, every data hook below is gated on an
  // empty `selectedDashboardId` until /api/dashboards returns — which is what
  // produced the long "Loading…" stall on cold boots.
  const [dashboards, setDashboards] = useState(() => {
    try {
      const raw = localStorage.getItem('cached_dashboards');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [selectedDashboardId, setSelectedDashboardId] = useState(() => {
    return localStorage.getItem('cached_selected_dashboard_id') || '';
  });
  const [userDefaults, setUserDefaults] = useState(() => {
    try {
      const raw = localStorage.getItem('cached_user_defaults');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });

  // Mirror dashboards / selection / settings back to localStorage so the
  // next cold boot starts fully populated.
  useEffect(() => {
    if (dashboards && dashboards.length) {
      localStorage.setItem('cached_dashboards', JSON.stringify(dashboards));
    }
  }, [dashboards]);
  useEffect(() => {
    if (selectedDashboardId) {
      localStorage.setItem('cached_selected_dashboard_id', selectedDashboardId);
    }
  }, [selectedDashboardId]);
  useEffect(() => {
    if (userDefaults && Object.keys(userDefaults).length) {
      localStorage.setItem('cached_user_defaults', JSON.stringify(userDefaults));
    }
  }, [userDefaults]);

  // ── Filter State ──
  const [showFilter, setShowFilter] = useState(false);
  const filterRef = useRef(null);
  // Seed the date range from the current billing cycle synchronously so
  // the first render already has the right window — otherwise the user
  // briefly sees every transaction before the effect catches up.
  const [filterFrom, setFilterFrom] = useState(() => {
    try {
      const raw = localStorage.getItem('cached_user_defaults');
      const defaults = raw ? JSON.parse(raw) : {};
      return getCycleDates(defaults).from;
    } catch {
      return getCycleDates({}).from;
    }
  });
  const [filterTo, setFilterTo] = useState(() => {
    try {
      const raw = localStorage.getItem('cached_user_defaults');
      const defaults = raw ? JSON.parse(raw) : {};
      return getCycleDates(defaults).to;
    } catch {
      return getCycleDates({}).to;
    }
  });
  const [filterCategory, setFilterCategory] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterAccountIds, setFilterAccountIds] = useState([]);

  // Tracks whether the user has manually picked a custom date range. When
  // false, the effect below re-pins the range to the current cycle whenever
  // the cycle setting changes.
  const cycleRangeManuallyOverridden = useRef(false);

  // ── Realtime Sync & Shared State ──
  // useBudgetSync runs the incremental /api/sync delta fetch + Supabase
  // realtime subscription against the canonical unfiltered cache.
  useBudgetSync(selectedDashboardId);

  usePendingQueueProcessor();

  // NOTE: Scheduled transactions are server-managed.
  //   - In-process scheduler in `backend/app/routers/scheduled.py:run_scheduler_loop`
  //     fires due rows once a minute while the server is awake.
  //   - On free-tier hosts that sleep, an external cron pings
  //     POST /api/scheduled-transactions/cron with X-Cron-Secret to wake
  //     the server AND advance any due schedules.
  // The frontend deliberately does NOT auto-trigger /process on dashboard
  // mount anymore — users were seeing 09:00 EMIs appear at 1:59 PM
  // whenever they happened to open the app.

  // ── TanStack Query Shared State ──
  // Single canonical unfiltered list — filtered views are derived below.
  const { data: transactionsRaw, isLoading: loadingTx } = useAllTransactions(selectedDashboardId);
  const { data: accountsRaw } = useAccounts(selectedDashboardId);
  const { data: consolidatedAccountsRaw } = useConsolidatedAccounts(selectedDashboardId);
  const { data: profileSettings } = useProfileSettings();

  useEffect(() => {
    if (profileSettings) {
      setUserDefaults(prev => ({ ...prev, ...profileSettings }));
    }
  }, [profileSettings]);

  // Re-pin the date range whenever the cycle setting changes. A change to
  // the cycle setting (from Settings) always wins — even over a manually
  // picked range — since the user just told the app "this is my new window."
  // The override flag still suppresses re-pinning on unrelated re-renders
  // (initial mount with the same cycle, profileSettings cache rehydration).
  const lastSyncedCycle = useRef(null);
  useEffect(() => {
    const sig = [
      userDefaults?.billing_cycle || '',
      userDefaults?.billing_manual_day || '',
      userDefaults?.billing_cycle_type || '',
      userDefaults?.billing_cycle_day || '',
    ].join('|');
    const cycleSettingChanged = lastSyncedCycle.current !== null && lastSyncedCycle.current !== sig;
    lastSyncedCycle.current = sig;
    if (cycleRangeManuallyOverridden.current && !cycleSettingChanged) return;
    const { from, to } = getCycleDates(userDefaults || {});
    cycleRangeManuallyOverridden.current = false;
    setFilterFrom(from);
    setFilterTo(to);
  }, [
    userDefaults?.billing_cycle, userDefaults?.billing_manual_day,
    userDefaults?.billing_cycle_type, userDefaults?.billing_cycle_day,
  ]);

  // Show the floater ONLY when we have a cold cache — i.e. no data to render.
  // Background refetches stay silent: the user keeps seeing the cached numbers
  // (which were hydrated instantly from IndexedDB by PersistQueryClientProvider).
  const isDashboardFetching =
    !selectedDashboardId ||
    transactionsRaw === undefined ||
    accountsRaw === undefined;

  // ── Budget State ──
  const [targetBudget, setTargetBudget] = useState(() => {
    return localStorage.getItem(`budget_${selectedDashboardId}`) || 50000;
  });


  // ── Derived State ──
  // useMemo so the reference is stable when transactionsRaw is undefined —
  // otherwise every render produces a fresh `[]` and downstream useMemo hooks
  // (like `filteredTransactions`) recompute every render.
  const transactions = useMemo(() => transactionsRaw || [], [transactionsRaw]);

  // Filtered view is computed in-memory from the canonical cache — no network
  // round-trip per filter change.
  const filteredTransactions = useMemo(() => {
    if (!transactions.length) return transactions;
    const from = filterFrom ? new Date(filterFrom) : null;
    const to = filterTo ? new Date(filterTo) : null;
    if (from) from.setHours(0, 0, 0, 0);
    if (to) to.setHours(23, 59, 59, 999);
    return transactions.filter(tx => {
      if (filterCategory && tx.category !== filterCategory) return false;
      if (filterType && tx.transaction_type !== filterType) return false;
      if (filterAccountIds.length > 0 && !filterAccountIds.includes(tx.account_id)) return false;
      if (from || to) {
        const txDate = tx.transaction_date || '';
        const d = txDate.includes('T') ? new Date(txDate) : new Date(txDate + 'T00:00:00');
        if (from || to) {
          if (isNaN(d.getTime())) return false;
          if (from && d < from) return false;
          if (to && d > to) return false;
        }
      }
      return true;
    });
  }, [transactions, filterFrom, filterTo, filterCategory, filterType, filterAccountIds]);

  const [showSettings, setShowSettings] = useState(false);
  const [showDefaults, setShowDefaults] = useState(false);
  const [editTx, setEditTx] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);

  // Manual reload for the All Transactions list. Invalidates the React-Query
  // caches that drive the list + balances + summary so the user gets fresh
  // server data without having to reload the whole page.
  const [txReloading, setTxReloading] = useState(false);
  const handleReloadAllTx = useCallback(async () => {
    if (txReloading || !selectedDashboardId) return;
    setTxReloading(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allTransactions(selectedDashboardId) }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts(selectedDashboardId) }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.consolidatedAccounts(selectedDashboardId) }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboardSummary(selectedDashboardId) }),
      ]);
    } finally {
      // Small minimum spin so the icon doesn't flicker on fast networks.
      setTimeout(() => setTxReloading(false), 400);
    }
  }, [txReloading, selectedDashboardId, queryClient]);

  // Reset the failure flag whenever the avatar URL changes so a new URL gets a fresh attempt
  useEffect(() => {
    setAvatarFailed(false);
  }, [user?.avatar_url]);

  // Stable callback for the Split Groups tab. Inline arrow functions change
  // reference every render, which was re-firing AddTransaction's
  // [activeMode, onActiveModeChange] effect and flashing the Split Groups
  // view away ~1-2s after opening.
  const handleSplitgroupsBack = useCallback((mode) => {
    if (mode === null) setActiveTab('dashboard');
  }, []);

  // Prompt OAuth users (no password yet) to set one up — but only on a fresh
  // sign-in (flag set by AuthContext). Refreshing the page will NOT re-prompt.
  useEffect(() => {
    if (user && user.has_password === false && sessionStorage.getItem('prompt_password_setup')) {
      setShowPasswordSetup(true);
      sessionStorage.removeItem('prompt_password_setup');
    }
    // If a background refresh reveals the user actually has a password,
    // close the modal immediately — e.g., backend just started returning
    // has_password=true after a deploy.
    if (user && user.has_password === true) {
      setShowPasswordSetup(false);
    }
  }, [user]);
  const [showSpendBreakdown, setShowSpendBreakdown] = useState(false);
  const [entryAnim, setEntryAnim] = useState(true);
  const [pageLoaded, setPageLoaded] = useState(false);

  // Two ways to count "spending":
  //
  //   items — count every individual purchase. A credit-card swipe or
  //           metro tap is the spending event. Bills and wallet loads
  //           are skipped (they would double-count).
  //
  //   bills — count when money leaves the bank as a bill or wallet load.
  //           Individual CC swipes / metro taps are skipped (they'll be
  //           captured at bill / load time).
  //
  // Both modes:
  //   • EXCLUDE pure bank-to-bank transfers (money still with you)
  //   • INCLUDE scheduled transactions as direct spending
  //
  // Persist the choice so the user picks once and the dashboard remembers.
  const [countingMode, setCountingMode] = useState(() => {
    try {
      const saved = localStorage.getItem('counting_mode');
      // Migrate legacy values from the previous Accrual / Cash naming.
      if (saved === 'cash' || saved === 'bills') return 'bills';
      return 'items';
    } catch { return 'items'; }
  });
  useEffect(() => {
    try { localStorage.setItem('counting_mode', countingMode); } catch { /* ignore */ }
  }, [countingMode]);

  // Account types whose individual purchases are NOT real cash outflows
  // (credit cards & prepaid balances). Spending on these accounts is
  // funded later (CC bill) or earlier (wallet load), so under "bills"
  // mode we count only the funding step, not the individual swipes.
  const DEFERRED_TYPES = useMemo(
    () => new Set(['credit', 'prepaid_wallet', 'metro_card', 'food_card']),
    []
  );

  // Set of `transfer_id` values where at least one leg touches a deferred
  // account (CC payment, metro/food/wallet load). Used by the classifier
  // to distinguish "bill or load" transfers (count under bills mode)
  // from pure bank-to-bank transfers (never counted, money still with us).
  const billTransferIds = useMemo(() => {
    const txs = filteredTransactions || [];
    const ids = new Set();
    txs.forEach(tx => {
      if (tx.transfer_id && DEFERRED_TYPES.has(tx.accounts?.account_type)) {
        ids.add(tx.transfer_id);
      }
    });
    return ids;
  }, [filteredTransactions, DEFERRED_TYPES]);

  // IDs of legacy "category === 'transfer'" rows that ARE actually part
  // of a real two-legged transfer (matched by date + amount + opposite
  // type). A solo `category === 'transfer'` row with no counterpart is
  // NOT a real transfer — it's a misnamed payment (e.g. user paying a
  // friend and just picking the wrong category). Those need to count
  // as spending, not get silently dropped.
  const legacyTransferPairIds = useMemo(() => {
    const txs = filteredTransactions || [];
    const candidates = txs.filter(t =>
      !t.transfer_id &&
      (t.category === 'transfer' || t.category === 'Credit Card Payment')
    );
    const matched = new Set();
    for (const tx of candidates) {
      if (matched.has(tx.id)) continue;
      const other = candidates.find(t =>
        t.id !== tx.id &&
        !matched.has(t.id) &&
        t.transaction_date === tx.transaction_date &&
        Math.abs(parseFloat(t.amount) - parseFloat(tx.amount)) < 0.01 &&
        t.transaction_type !== tx.transaction_type
      );
      if (other) {
        matched.add(tx.id);
        matched.add(other.id);
      }
    }
    return matched;
  }, [filteredTransactions]);

  const classifyForSpending = useCallback((tx) => {
    // Scheduled transactions are direct spending under both modes per
    // the user's explicit rule — they fire as planned recurring spend
    // regardless of which account they hit.
    if (tx.is_scheduled || tx.scheduled_id) {
      return tx.transaction_type === 'credit' ? 'income' : 'spend';
    }

    // Only count as a transfer if it's actually paired:
    //   • modern: has a transfer_id linking the two legs, OR
    //   • legacy: has a confirmed counterpart in `legacyTransferPairIds`.
    // A solo `category === 'transfer'` row with no counterpart is a
    // misnamed payment — treat it as a regular debit/credit so it
    // contributes to spending instead of being silently dropped.
    const isTransfer = !!tx.transfer_id || legacyTransferPairIds.has(tx.id);
    const acctType = tx.accounts?.account_type;
    const isDeferredAccount = DEFERRED_TYPES.has(acctType);

    if (isTransfer) {
      // The deferred-side leg (CC accepting bill payment, metro card
      // receiving load) is always excluded. The bank-side leg may or
      // may not count depending on whether this is a bill/load vs a
      // pure inter-bank transfer.
      if (isDeferredAccount) return 'excluded';

      // Is this transfer a bill or a load? It is iff its other leg
      // touches a deferred account.
      const isBillOrLoad = tx.transfer_id
        ? billTransferIds.has(tx.transfer_id)
        : tx.category === 'Credit Card Payment'; // legacy hint

      // Pure bank-to-bank transfer — never counts (per user's rule:
      // "all the transfers are nowhere getting considered").
      if (!isBillOrLoad) return 'excluded';

      // It is a bill / load. Bills mode counts the bank-side debit;
      // Items mode skips it (the individual swipes/taps elsewhere
      // in the list will be counted instead).
      if (countingMode === 'bills' && tx.transaction_type === 'debit') {
        return 'spend';
      }
      return 'excluded';
    }

    // Non-transfer: a normal purchase or income.
    if (isDeferredAccount) {
      // CC swipe, metro tap, food-card scan, prepaid spend.
      if (countingMode === 'bills') return 'excluded';
      return tx.transaction_type === 'credit' ? 'income' : 'spend';
    }

    // Regular bank/cash/upi/debit-card transaction.
    return tx.transaction_type === 'credit' ? 'income' : 'spend';
  }, [countingMode, billTransferIds, legacyTransferPairIds, DEFERRED_TYPES]);

  // ── Bucket classifier ─────────────────────────────────────────
  // Maps each transaction to a single bucket key shown in the
  // breakdown modal. Used both to (a) sum amounts per bucket and
  // (b) filter the All Transactions list when the user taps a
  // bucket as a filter.
  const getBucketKey = useCallback((tx) => {
    const cls = classifyForSpending(tx);
    const isScheduled = !!(tx.is_scheduled || tx.scheduled_id);
    const isTransferRow = !!tx.transfer_id || legacyTransferPairIds.has(tx.id);
    const isDeferred = DEFERRED_TYPES.has(tx.accounts?.account_type);
    const isCredit = tx.transaction_type === 'credit';

    if (cls === 'spend') {
      if (isScheduled) return 'scheduled';
      if (isTransferRow) return 'bill_load';      // bank-side leg of a bill/load
      if (isDeferred) return 'card_swipe';
      return 'direct';
    }
    if (cls === 'income') return 'income';
    // excluded
    if (isTransferRow) {
      if (isDeferred || isCredit) return 'transfer_other';
      return 'pure_transfer';
    }
    if (isDeferred) return 'swipe_skip';
    return 'transfer_other';
  }, [classifyForSpending, legacyTransferPairIds, DEFERRED_TYPES]);

  // Bucket-as-filter — when the user taps a bucket in the modal, narrow
  // the visible list (and the headline / right-edge strips) to just
  // that bucket. Stored separately from the other filters because the
  // existing filter set lives on raw fields (category, type, etc.) and
  // a bucket can span multiple fields.
  const [bucketFilter, setBucketFilter] = useState(null);

  // Display set the list, headline and per-row strips actually render
  // against. Equals filteredTransactions unless a bucket filter is
  // active, in which case it's narrowed to just that bucket.
  const displayTransactions = useMemo(() => {
    if (!bucketFilter) return filteredTransactions;
    return (filteredTransactions || []).filter(tx => getBucketKey(tx) === bucketFilter);
  }, [filteredTransactions, bucketFilter, getBucketKey]);

  const filteredSummary = useMemo(() => {
    let debits = 0;
    let credits = 0;
    const txs = displayTransactions || [];
    txs.forEach(tx => {
      const amt = parseFloat(tx.amount) || 0;
      const cls = classifyForSpending(tx);
      if (cls === 'spend') debits += amt;
      else if (cls === 'income') credits += amt;
    });
    return { count: txs.length, debits, credits, net: credits - debits };
  }, [displayTransactions, classifyForSpending]);

  // Static bucket metadata — labels & hints surface in the modal.
  const BUCKET_META = useMemo(() => ({
    direct:        { side: 'spend',    label: 'Direct purchases',        hint: 'Bank, cash, UPI, debit card', icon: '💳' },
    card_swipe:    { side: 'spend',    label: 'Card / wallet purchases', hint: 'CC swipes, metro taps, food card', icon: '🛒' },
    bill_load:     { side: 'spend',    label: 'Bills & wallet loads',    hint: 'Bank-side payment of CC bill / wallet load', icon: '🧾' },
    scheduled:     { side: 'spend',    label: 'Scheduled transactions',  hint: 'Auto-fired by the server scheduler', icon: '🗓️' },
    income:        { side: 'excluded', label: 'Income',                  hint: 'Salary, refunds, credits', icon: '💰' },
    pure_transfer: { side: 'excluded', label: 'Pure transfers',          hint: 'Bank-to-bank, money still with you', icon: '🔄' },
    swipe_skip:    { side: 'excluded', label: 'Card / wallet purchases', hint: 'Counted at the bill / load step under Bills mode', icon: '🛒' },
    transfer_other:{ side: 'excluded', label: 'Receiving side',          hint: 'Mirror leg of a paired transfer', icon: '↩️' },
  }), []);

  // Sum + count per bucket. Computes from the BASE filtered set so the
  // breakdown modal stays useful (lets the user switch buckets) even
  // while a bucket-as-filter is currently applied.
  const spendingBreakdown = useMemo(() => {
    const out = {};
    Object.keys(BUCKET_META).forEach(k => { out[k] = { count: 0, sum: 0, ...BUCKET_META[k] }; });
    (filteredTransactions || []).forEach(tx => {
      const amt = parseFloat(tx.amount) || 0;
      const key = getBucketKey(tx);
      if (out[key]) {
        out[key].count += 1;
        out[key].sum += amt;
      }
    });
    return out;
  }, [filteredTransactions, getBucketKey, BUCKET_META]);



  const financialOverview = useMemo(() => {
    let balance = 0;
    let owed = 0;
    if (accountsRaw) {
      accountsRaw.forEach(acc => {
        if (filterAccountIds.length > 0 && !filterAccountIds.includes(acc.id)) return;
        const bal = parseFloat(acc.current_balance) || 0;
        if (acc.mechanism === 'pay_later') {
          owed += bal;
        } else {
          balance += bal;
        }
      });
    }
    return { balance, owed };
  }, [accountsRaw, filterAccountIds]);

  const handleSetBudget = () => {
    const val = window.prompt("Enter your monthly budget target:", targetBudget);
    if (val !== null && !isNaN(val)) {
      setTargetBudget(val);
      localStorage.setItem(`budget_${selectedDashboardId}`, val);
    }
  };



  useEffect(() => {
    // Stage 1: Trigger page content fade-in slightly after mount
    const pageTimer = setTimeout(() => setPageLoaded(true), 300);
    // Stage 2: Delay logo move so the 'center' state is clearly seen
    const logoTimer = setTimeout(() => setEntryAnim(false), 1200);
    
    return () => {
      clearTimeout(logoTimer);
      clearTimeout(pageTimer);
    };
  }, []);


  // Refs for closing dropdowns on outside click
  const profileMenuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const fetchDashboards = useCallback(async () => {
    try {
      // 60s timeout for the cold-boot case where the backend is waking from
      // sleep. The default 15s wasn't enough and the request failed silently,
      // leaving `dashboards` empty and the page in a permanent gated state.
      const { data } = await api.get('/api/dashboards', { timeout: 60000 });
      let list = data || [];
      if (list.length === 0) {
        try {
          const { data: newBoard } = await api.post('/api/dashboards', { name: 'Personal' }, { timeout: 60000 });
          list = [newBoard];
        } catch (_) { }
      }
      setDashboards(list);
    } catch (_) { }
  }, []);


  useEffect(() => {
    fetchDashboards();
  }, [fetchDashboards]);

  // If the cold-boot fetch for dashboards failed (sleeping backend, flaky
  // network), retry when the tab regains focus or the network comes back.
  // Profile settings now ride on React Query (`useProfileSettings`), which
  // handles its own retry/refetch — no manual retry needed for it here.
  useEffect(() => {
    const retryIfMissing = () => {
      if (document.visibilityState !== 'visible') return;
      if (!dashboards || dashboards.length === 0) fetchDashboards();
    };
    document.addEventListener('visibilitychange', retryIfMissing);
    window.addEventListener('online', retryIfMissing);
    window.addEventListener('focus', retryIfMissing);
    return () => {
      document.removeEventListener('visibilitychange', retryIfMissing);
      window.removeEventListener('online', retryIfMissing);
      window.removeEventListener('focus', retryIfMissing);
    };
  }, [dashboards, fetchDashboards]);

  // Handle initial dashboard selection
  useEffect(() => {
    if (dashboards.length > 0) {
      const defId = userDefaults.default_dashboard_id;
      // If no selection yet, OR if defId arrived and we're on a non-default one, maybe switch?
      // For now, just set initial selection reliably.
      if (!selectedDashboardId) {
        if (defId && dashboards.find(d => d.id === defId)) {
          setSelectedDashboardId(defId);
        } else {
          setSelectedDashboardId(dashboards[0].id);
        }
      }
    }
  }, [dashboards, userDefaults, selectedDashboardId]);



  useEffect(() => {
    function handleClickOutsideFilter(e) {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowFilter(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutsideFilter);
    return () => document.removeEventListener('mousedown', handleClickOutsideFilter);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const displayName = user?.name || user?.email || 'User';
  const initials = (displayName[0] || '?').toUpperCase();

  return (
    <div className={`db-root db-tab-${activeTab}`}>

      {/* Logo - Positioned outside header for viewport-relative centering during splash */}
      <div className={`db-topbar-logo ${entryAnim ? 'entry' : ''}`} onClick={() => {
        setResetKey(prev => prev + 1);
        setActiveTab('dashboard');
        setMobileDashMode(null);
        setFilterFrom(null);
        setFilterTo(null);
        setFilterCategory('');
        setFilterType('');
        setFilterAccountIds([]);
        setShowFilter(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}>
        Budgetify
      </div>

      <header className={`db-topbar ${scrolled ? 'scrolled' : ''} ${entryAnim ? 'anim-blur' : ''}`}>
        <div className="db-topbar-left-side">
          <div className="db-topbar-logo-spacer" />
        </div>

        <nav className="db-pill-nav">
          {NAV_TABS.map(tab => (
            <button
              key={tab}
              className={`db-pill-btn${activeTab === tab ? ' active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'all_transactions' ? 'All Transactions' : 
               tab === 'splitgroups' ? 'Split Groups' : 
               tab === 'lend_borrow' ? 'Lend & Borrow' :
               tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>

        <div className="db-topbar-right" ref={profileMenuRef}>
          <PendingUploadsIndicator />

          {user?.avatar_url && !avatarFailed ? (
            <img src={user.avatar_url} alt={displayName} className="db-nav-avatar db-nav-avatar-click"
              referrerPolicy="no-referrer"
              onClick={() => setShowProfileMenu(v => !v)}
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <div className="db-nav-initials db-nav-avatar-click"
              onClick={() => setShowProfileMenu(v => !v)}>{initials}</div>
          )}

          {showProfileMenu && (
            <div className="db-profile-dropdown">
              <div className="db-profile-dd-header">
                <div className="db-profile-dd-name">{displayName}</div>
                <div className="db-profile-dd-email">{user?.email}</div>
              </div>
              <button className="db-profile-dd-item" onClick={() => { setShowProfileMenu(false); setShowSettings(true); }}>
                <span>⚙️</span> Settings
              </button>
              <button className="db-profile-dd-item" onClick={() => { setShowProfileMenu(false); setShowDefaults(true); }}>
                <span>✨</span> Defaults
              </button>
              <button className="db-profile-dd-item" onClick={(e) => { e.stopPropagation(); toggleTheme(); }}>
                <span>{theme === 'light' ? '🌙' : '☀️'}</span> {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
              </button>
              <hr className="db-profile-divider" />
              <div className="db-profile-dd-footer">
                <button className="db-profile-dd-logout-small" onClick={() => { setShowProfileMenu(false); handleLogout(); }}>
                  Sign out 🚪
                </button>
              </div>
            </div>
          )}
        </div>

      </header>

      <main className={`db-main ${pageLoaded ? 'page-fade-in' : ''} ${entryAnim ? 'anim-blur' : ''}`}>
        {/* Global Sticky Header Summary - Only on All Transactions tab */}





        {activeTab === 'dashboard' && (
          <div className="db-dash-content">
            {isMobile && !mobileDashMode ? (
              <MobileDashboard
                sym={sym}
                balance={financialOverview.balance}
                owed={financialOverview.owed}
                spent={filteredSummary.debits}
                income={filteredSummary.credits}
                accounts={accountsRaw || []}
                consolidatedAccounts={consolidatedAccountsRaw || []}
                user={user}
                dashboards={dashboards}
                selectedDashboardId={selectedDashboardId}
                onDashboardChange={setSelectedDashboardId}
                onAccountClick={(idOrIds) => {
                  setFilterAccountIds(Array.isArray(idOrIds) ? idOrIds : [idOrIds]);
                  setActiveTab('all_transactions');
                  setShowFilter(false);
                }}
                onModeSelect={(modeId) => setMobileDashMode(modeId)}
                onDashboardsChanged={async () => {
                  const { data } = await api.get('/api/dashboards');
                  const list = data || [];
                  setDashboards(list);
                  if (selectedDashboardId && !list.find(d => d.id === selectedDashboardId)) {
                    setSelectedDashboardId(list[0]?.id || null);
                  }
                }}
              />
            ) : (
              <AddTransaction
                key={resetKey}
                propDashboardId={selectedDashboardId}
                onDashboardChange={setSelectedDashboardId}
                propActiveMode={mobileDashMode}
                onActiveModeChange={isMobile ? setMobileDashMode : undefined}
                spendingHeadline={filteredSummary.debits}
                balance={financialOverview.balance}
                owed={financialOverview.owed}
                income={filteredSummary.credits}
                onShowBreakdown={() => setShowSpendBreakdown(true)}
                countingMode={countingMode}
                setCountingMode={setCountingMode}
                onAccountClick={(idOrIds) => {
                  setFilterAccountIds(Array.isArray(idOrIds) ? idOrIds : [idOrIds]);
                  setActiveTab('all_transactions');
                  setShowFilter(false);
                }}
              />
            )}
          </div>
        )}



        {activeTab === 'widgets' && (
          <div className="db-dash-content">
            {(() => {
              const { from, to } = getCycleDates(userDefaults);
              return (
                <WidgetsSection
                  transactions={transactions}
                  from={from}
                  to={to}
                  classifyForSpending={classifyForSpending}
                  onShowBreakdown={() => setShowSpendBreakdown(true)}
                  budget={targetBudget}
                  onSetBudget={handleSetBudget}
                />
              );
            })()}
          </div>
        )}

        {activeTab === 'all_transactions' && (
          <div className="db-tx-list-wrap">
            <div className="db-all-list-scroll">

              {/* Flowing Summary Hero - Dashboard Style */}
              <div className="db-tx-list-hero" onClick={() => setShowFullHeroStats(!showFullHeroStats)} style={{ cursor: 'pointer' }}>
                <div className="db-hero-card">
                  <div className="db-hero-card-glow" />
                  <div className="db-hero-card-inner">
                    <div className="db-hero-top">
                      <div className="db-hero-greeting-box">
                        <div className="db-hero-greeting-sm">Financial Overview</div>
                      </div>
                      <div className="db-hero-actions">
                        {dashboards.length > 0 && (
                          <select
                            className="db-hero-dash-select"
                            value={selectedDashboardId}
                            onChange={(e) => setSelectedDashboardId(e.target.value)}
                          >
                            {dashboards.map(d => (
                              <option key={d.id} value={d.id}>{d.name}</option>
                            ))}
                          </select>
                        )}

                        <button
                          className={`db-hero-reload-btn${txReloading ? ' spinning' : ''}`}
                          onClick={(e) => { e.stopPropagation(); handleReloadAllTx(); }}
                          disabled={txReloading}
                          aria-label="Reload all transactions"
                          title="Pull a fresh copy of every transaction from the server"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10" />
                            <polyline points="1 20 1 14 7 14" />
                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                          </svg>
                          <span className="db-hero-reload-label">Reload</span>
                        </button>

                        <div className="db-hero-filter-wrap" ref={filterRef}>
                          <button
                            className={`db-hero-filter-btn${(filterCategory || filterType || filterAccountIds.length > 0 || showFilter) ? ' active' : ''}`}
                            onClick={() => setShowFilter(v => !v)}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
                            Filter
                          </button>
                          {(filterCategory || filterType || filterAccountIds.length > 0 ||
                            (filterFrom && filterFrom !== getCycleDates(userDefaults || {}).from) ||
                            (filterTo && filterTo !== getCycleDates(userDefaults || {}).to)) && (
                            <button
                              className="db-hero-filter-clear-btn"
                              onClick={() => {
                                const { from, to } = getCycleDates(userDefaults || {});
                                cycleRangeManuallyOverridden.current = false;
                                setFilterCategory('');
                                setFilterType('');
                                setFilterFrom(from);
                                setFilterTo(to);
                                setFilterAccountIds([]);
                                setShowFilter(false);
                              }}
                            >
                              ✕ Clear
                            </button>
                          )}
                          
                          {showFilter && (
                            <div className="db-hero-filter-panel">
                              <div className="db-filter-section">
                                <div className="db-filter-label">Date Range</div>
                                <div className="db-filter-presets">
                                  {[
                                    { label: 'Current Cycle', get: () => getCycleDates(userDefaults) },
                                    { label: 'This Month', get: () => { const n = new Date(); return { from: fmtDate(new Date(n.getFullYear(), n.getMonth(), 1)), to: fmtDate(new Date(n.getFullYear(), n.getMonth() + 1, 0)) }; } },
                                    { label: 'Last Month', get: () => { const n = new Date(); return { from: fmtDate(new Date(n.getFullYear(), n.getMonth() - 1, 1)), to: fmtDate(new Date(n.getFullYear(), n.getMonth(), 0)) }; } },
                                    { label: 'Last 3 Months', get: () => { const n = new Date(); return { from: fmtDate(new Date(n.getFullYear(), n.getMonth() - 3, 1)), to: fmtDate(new Date()) }; } },
                                    { label: 'This Year', get: () => { const n = new Date(); return { from: fmtDate(new Date(n.getFullYear(), 0, 1)), to: fmtDate(new Date(n.getFullYear(), 11, 31)) }; } },
                                    { label: 'All Time', get: () => ({ from: '', to: '' }) },
                                  ].map(p => {
                                    const { from, to } = p.get();
                                    const active = filterFrom === from && filterTo === to;
                                    const isCyclePreset = p.label === 'Current Cycle';
                                    return (
                                      <button key={p.label} className={`db-filter-preset${active ? ' active' : ''}`}
                                        onClick={() => {
                                          cycleRangeManuallyOverridden.current = !isCyclePreset;
                                          setFilterFrom(from);
                                          setFilterTo(to);
                                        }}>
                                        {p.label}
                                      </button>
                                    );
                                  })}
                                </div>
                                <div className="db-filter-date-row">
                                  <div className="db-filter-date-group">
                                    <label className="db-filter-date-label">From</label>
                                    <input type="date" className="db-filter-date-input" value={filterFrom || ''}
                                      onChange={e => { cycleRangeManuallyOverridden.current = true; setFilterFrom(e.target.value); }} />
                                  </div>
                                  <div className="db-filter-date-group">
                                    <label className="db-filter-date-label">To</label>
                                    <input type="date" className="db-filter-date-input" value={filterTo || ''}
                                      onChange={e => { cycleRangeManuallyOverridden.current = true; setFilterTo(e.target.value); }} />
                                  </div>
                                </div>
                              </div>
                              <div className="db-filter-section">
                                <div className="db-filter-label">Type</div>
                                <div className="db-filter-presets">
                                  {['', 'debit', 'credit'].map(t => (
                                    <button key={t} className={`db-filter-preset${filterType === t ? ' active' : ''}`}
                                      onClick={() => setFilterType(t)}>
                                      {t === '' ? 'All' : t === 'debit' ? 'Expense' : 'Income'}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="db-filter-section">
                                <div className="db-filter-label">Category</div>
                                <div className="db-filter-presets db-filter-cats">
                                  <button className={`db-filter-preset${filterCategory === '' ? ' active' : ''}`}
                                    onClick={() => setFilterCategory('')}>All</button>
                                  {CATEGORIES.map(c => (
                                    <button key={c.id} className={`db-filter-preset${filterCategory === c.id ? ' active' : ''}`}
                                      onClick={() => setFilterCategory(c.id)}>
                                      {c.icon} {c.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="db-filter-section">
                                <div className="db-filter-label">Account</div>
                                <div className="db-filter-presets">
                                  <button className={`db-filter-preset${filterAccountIds[0] === '' ? ' active' : ''}`}
                                    onClick={() => setFilterAccountIds([])}>All</button>
                                  {accountsRaw?.map(acc => (
                                    <button key={acc.id} className={`db-filter-preset${filterAccountIds.length === 1 && filterAccountIds[0] === acc.id ? ' active' : ''}`}
                                      onClick={() => setFilterAccountIds([acc.id])}>
                                      {acc.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <button className="db-filter-clear"
                                onClick={() => {
                                  const { from, to } = getCycleDates(userDefaults);
                                  cycleRangeManuallyOverridden.current = false;
                                  setFilterFrom(from);
                                  setFilterTo(to);
                                  setFilterCategory('');
                                  setFilterType('');
                                  setFilterAccountIds([]);
                                  setShowFilter(false);
                                }}>
                                Clear all filters
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="db-hero-main-stats">
                      <div className="db-hero-main-stat">
                        <div className="db-hero-stat-label">
                          Spending
                          <button
                            className={`db-hero-mode-toggle ${countingMode}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCountingMode(m => m === 'items' ? 'bills' : 'items');
                            }}
                            title={
                              countingMode === 'items'
                                ? 'Items mode: every individual purchase counts (CC swipes, metro taps included). CC bills and wallet loads are skipped to avoid double-count. Click to switch to Bills.'
                                : 'Bills mode: counts CC bill payments and wallet loads (the bank-side debit). Individual CC swipes / metro taps are skipped — they\'re captured at bill / load time. Click to switch to Items.'
                            }
                          >
                            {countingMode === 'items' ? '🛒 Items' : '💳 Bills'}
                          </button>
                          <button className="db-hero-budget-edit" onClick={(e) => { e.stopPropagation(); handleSetBudget(); }}>edit</button>
                        </div>
                        <div className="db-hero-stat-value large debit">
                          {sym}{formatAmount(filteredSummary.debits)}
                        </div>
                      </div>
                      
                      <div className="db-hero-stats-grid">
                        <div className="db-hero-secondary-stat">
                          <span className="db-hero-sec-label">Balance</span>
                          <span className={`db-hero-sec-value ${financialOverview.balance < 0 ? 'debit' : 'credit'}`}>
                            {sym}{formatAmount(financialOverview.balance)}
                          </span>
                        </div>
                        <div className="db-hero-secondary-stat">
                          <span className="db-hero-sec-label">Owed</span>
                          <span className="db-hero-sec-value-owed">{sym}{formatAmount(financialOverview.owed)}</span>
                        </div>
                        <div className="db-hero-secondary-stat clickable" onClick={(e) => { e.stopPropagation(); setShowSpendBreakdown(true); }}>
                          <span className="db-hero-sec-label">Spent <span className="db-hero-arrow">▾</span></span>
                          <span className="db-hero-sec-value-spent">{sym}{formatAmount(filteredSummary.debits)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {(() => {
                const hasAnyFilter = filterFrom || filterTo || filterType || filterCategory || filterAccountIds.length > 0;
                if (!hasAnyFilter) return null;
                const cycle = getCycleDates(userDefaults || {});
                const isCycle = filterFrom === cycle.from && filterTo === cycle.to;
                const fmtChipDate = (s) => {
                  if (!s) return '—';
                  const d = new Date(s + 'T00:00:00');
                  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                };
                const catLabel = CATEGORIES.find(c => c.id === filterCategory);
                const accNames = filterAccountIds
                  .map(id => (accountsRaw || []).find(a => a.id === id)?.name)
                  .filter(Boolean);
                return (
                  <div className="db-tx-filter-chip-row">
                    <span className="db-tx-filter-chip-summary">
                      Showing <b>{displayTransactions.length}</b> of {transactions.length}
                    </span>

                    {(filterFrom || filterTo) && (
                      <div className={`db-tx-filter-chip${isCycle ? ' is-cycle' : ''}`}>
                        <span className="db-tx-filter-chip-ico">📅</span>
                        <span className="db-tx-filter-chip-label">
                          {isCycle ? 'Cycle' : 'Date'}
                          <b>{fmtChipDate(filterFrom)} → {fmtChipDate(filterTo)}</b>
                        </span>
                        <button
                          className="db-tx-filter-chip-x"
                          title={isCycle ? 'Cycle window (from Settings)' : 'Reset to current cycle'}
                          onClick={() => {
                            const { from, to } = getCycleDates(userDefaults || {});
                            cycleRangeManuallyOverridden.current = false;
                            setFilterFrom(from);
                            setFilterTo(to);
                          }}
                        >↺</button>
                      </div>
                    )}

                    {filterType && (
                      <div className="db-tx-filter-chip">
                        <span className="db-tx-filter-chip-ico">{filterType === 'credit' ? '↙' : '↗'}</span>
                        <span className="db-tx-filter-chip-label">
                          Type
                          <b>{filterType === 'credit' ? 'Income' : 'Expense'}</b>
                        </span>
                        <button className="db-tx-filter-chip-x" onClick={() => setFilterType('')}>✕</button>
                      </div>
                    )}

                    {filterCategory && (
                      <div className="db-tx-filter-chip">
                        <span className="db-tx-filter-chip-ico">{catLabel?.icon || '🏷️'}</span>
                        <span className="db-tx-filter-chip-label">
                          Category
                          <b>{catLabel?.label || filterCategory}</b>
                        </span>
                        <button className="db-tx-filter-chip-x" onClick={() => setFilterCategory('')}>✕</button>
                      </div>
                    )}

                    {filterAccountIds.length > 0 && (
                      <div className="db-tx-filter-chip">
                        <span className="db-tx-filter-chip-ico">🏦</span>
                        <span className="db-tx-filter-chip-label">
                          Account
                          <b>{accNames.length <= 2 ? accNames.join(', ') : `${accNames.length} accounts`}</b>
                        </span>
                        <button className="db-tx-filter-chip-x" onClick={() => setFilterAccountIds([])}>✕</button>
                      </div>
                    )}

                    <button
                      className="db-tx-filter-chip-reset"
                      onClick={() => {
                        const { from, to } = getCycleDates(userDefaults || {});
                        cycleRangeManuallyOverridden.current = false;
                        setFilterFrom(from);
                        setFilterTo(to);
                        setFilterType('');
                        setFilterCategory('');
                        setFilterAccountIds([]);
                      }}
                    >
                      Reset all
                    </button>
                  </div>
                );
              })()}

              {bucketFilter && (
                <div className="db-bucket-filter-chip-row">
                  <div className="db-bucket-filter-chip">
                    <span className="db-bfc-icon">{spendingBreakdown[bucketFilter]?.icon}</span>
                    <span className="db-bfc-label">Showing <b>{spendingBreakdown[bucketFilter]?.label}</b> only</span>
                    <button className="db-bfc-clear" onClick={() => setBucketFilter(null)}>✕</button>
                  </div>
                </div>
              )}
              {loadingTx ? (

              <div className="db-list-loading">
                <div className="db-spinner"></div>
                <p>Loading transactions...</p>
              </div>
            ) : displayTransactions.length === 0 ? (
              <div className="db-coming-soon">
                <div className="db-empty-icon">📂</div>
                <p>No transactions found</p>
                {(filterCategory || filterType || filterFrom || filterTo || filterAccountIds.length > 0 || bucketFilter) && (
                  <button className="db-clear-filters-link" onClick={() => {
                    const { from, to } = getCycleDates(userDefaults);
                    cycleRangeManuallyOverridden.current = false;
                    setFilterCategory('');
                    setFilterType('');
                    setFilterFrom(from);
                    setFilterTo(to);
                    setFilterAccountIds([]);
                    setBucketFilter(null);
                  }}>
                    Clear all filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="db-tx-list">
                  <div className="db-tx-legend">
                    <div className="db-legend-item"><span className="db-legend-dot credit"></span> Income</div>
                    <div className="db-legend-item"><span className="db-legend-dot debit"></span> Expense</div>
                    <div className="db-legend-item"><span className="db-legend-dot transfer"></span> Transfer</div>
                    <div className="db-legend-item"><span className="db-legend-dot scheduled"></span> Scheduled</div>
                  </div>
                  {(() => {
                    const groupsMap = new Map();
                    const processedTransfers = new Set(); // by transfer_id
                    const processedLegacyTransfers = new Set(); // by id
                    displayTransactions.forEach(tx => {
                      // 1. Group by transfer_id (new system)
                      if (tx.transfer_id) {
                        if (processedTransfers.has(tx.transfer_id)) return;
                        processedTransfers.add(tx.transfer_id);
                      } 
                      // 2. Fallback grouping for legacy transfers (category === 'transfer')
                      else if (tx.category === 'transfer' || tx.category === 'Credit Card Payment') {
                        if (processedLegacyTransfers.has(tx.id)) return;
                        // Look for a potential match in the same view
                        const other = displayTransactions.find(t => 
                          t.id !== tx.id && 
                          (t.category === 'transfer' || t.category === 'Credit Card Payment') && 
                          t.transaction_date === tx.transaction_date &&
                          Math.abs(parseFloat(t.amount) - parseFloat(tx.amount)) < 0.01 &&
                          t.transaction_type !== tx.transaction_type &&
                          !processedLegacyTransfers.has(t.id) &&
                          !t.transfer_id
                        );
                        if (other) {
                          processedLegacyTransfers.add(tx.id);
                          processedLegacyTransfers.add(other.id);
                        }
                      }
                      
                      const dateKey = tx.transaction_date?.slice(0, 10) || 'unknown';
                      if (!groupsMap.has(dateKey)) {
                        groupsMap.set(dateKey, []);
                      }
                      groupsMap.get(dateKey).push(tx);
                    });
                    
                    const groups = Array.from(groupsMap.entries()).map(([dateKey, txs]) => ({ dateKey, txs }));
                    
                    return groups.map(group => (
                      <div key={group.dateKey} className="db-tx-group">
                        <div className="db-tx-date-sep">
                          <span className="db-tx-date-sep-line" />
                          <span className="db-tx-date-sep-label">{formatDateGroup(group.dateKey)}</span>
                          <span className="db-tx-date-sep-line" />
                        </div>
                        {group.txs.map(tx => {
                          const isNewTransfer = !!tx.transfer_id;
                          const isLegacyTransfer = processedLegacyTransfers.has(tx.id);
                          const isTransfer = isNewTransfer || isLegacyTransfer;
                          
                          const isCr = tx.transaction_type === 'credit';
                          const acc = tx.accounts || {};
                          const cat = CATEGORIES.find(c => c.id === tx.category) || { icon: '📦', label: tx.category };
                          const msgCount = (tx.msg_count && tx.msg_count[0]) ? tx.msg_count[0].count : 0;
                          
                          // Find the "other" account for display
                          let transferToName = '';
                          if (isTransfer) {
                            let other;
                            if (isNewTransfer) {
                              other = filteredTransactions.find(t => t.transfer_id === tx.transfer_id && t.id !== tx.id);
                            } else {
                              other = filteredTransactions.find(t => 
                                t.id !== tx.id && 
                                (t.category === 'transfer' || t.category === 'Credit Card Payment') && 
                                t.transaction_date === tx.transaction_date && 
                                Math.abs(parseFloat(t.amount) - parseFloat(tx.amount)) < 0.01 && 
                                t.transaction_type !== tx.transaction_type && 
                                !t.transfer_id
                              );
                            }
                            if (other) {
                              transferToName = other.accounts?.name || 'Unknown';
                            }
                          }

                          // Spending classification — drives the right-edge
                          // strip that shows whether THIS row contributes to
                          // the Spending headline under the active mode.
                          const spendCls = classifyForSpending(tx);
                          return (
                            <div key={tx.id} className={`db-tx-item${tx._pending ? ' is-pending' : ''}${isTransfer ? ' is-transfer' : ''}${tx.is_scheduled || tx.scheduled_id ? ' is-scheduled' : ''}`}>
                              <button className="db-tx-row" onClick={() => setEditTx(tx)}>
                                <div className="db-tx-icon-cell">
                                  <span className="db-tx-icon">{isTransfer ? '🔄' : cat.icon}</span>
                                </div>
                                <div className="db-tx-info-cell">
                                  <div className="db-tx-desc">
                                    {isTransfer ? (
                                      <span className="db-tx-transfer-desc">
                                        {tx.transaction_type === 'debit' ? `${acc.name} → ${transferToName}` : `${transferToName} → ${acc.name}`}
                                      </span>
                                    ) : (tx.description || cat.label)}
                                  </div>
                                  <div className="db-tx-sub">
                                    <span className="db-tx-acc">{acc.name || 'Unknown'}</span>
                                    {!isTransfer && (
                                      <>
                                        <span className="db-tx-sep">·</span>
                                        <span className="db-tx-cat">{cat.label}</span>
                                      </>
                                    )}
                                    {tx.tags && (
                                      <>
                                        <span className="db-tx-sep">·</span>
                                        <span className="db-tx-tag" style={{
                                          color: tx.tags.color,
                                          background: `${tx.tags.color}15`,
                                          padding: '1px 6px',
                                          borderRadius: '4px',
                                          fontSize: '0.75rem',
                                          fontWeight: '700'
                                        }}>
                                          {tx.tags.name.toUpperCase()}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="db-tx-amount-cell">
                                  <div className={`db-tx-amount ${isTransfer ? 'transfer' : (isCr ? 'credit' : 'debit')}`}>
                                    {sym}{formatAmount(tx.amount)}
                                  </div>
                                  <div className="db-tx-date-row">
                                    <span className="db-tx-time" style={{ opacity: 0.6, fontSize: '0.85em' }}>
                                      {new Date(tx.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    {msgCount > 0 && <span className="db-tx-msg-badge">💬 {msgCount}</span>}
                                  </div>
                                </div>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ));
                  })()}
                </div>
                <div className="db-tx-end">
                  <div className="db-tx-end-squeeze" />
                  <span className="db-tx-end-label">End</span>
                  <div className="db-tx-end-squeeze" />
                </div>
              </>
            )}

            </div>
          </div>
        )}


        {activeTab === 'splitgroups' && (
          <div className="db-dash-content">
            <AddTransaction
              key={`splitgroups-${resetKey}`}
              propDashboardId={selectedDashboardId}
              onDashboardChange={setSelectedDashboardId}
              propActiveMode="splitgroups"
              onActiveModeChange={handleSplitgroupsBack}
            />
          </div>
        )}
      </main>



      {showSettings && <Settings onClose={() => setShowSettings(false)} />}
      {showDefaults && <Defaults onClose={() => setShowDefaults(false)} />}
      {editTx && (
        <TransactionModal
          transaction={editTx}
          onClose={() => setEditTx(null)}
          onUpdate={() => { 
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.allTransactions(selectedDashboardId) });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.dashboardSummary(selectedDashboardId) });
          }}
        />
      )}
      {showPasswordSetup && (
        <PasswordSetupModal onClose={() => setShowPasswordSetup(false)} />
      )}
      <FetchingIndicator show={isDashboardFetching} />

      {/* Bottom-right pill that opens the breakdown modal. Shown only
          on the All Transactions tab per user request. */}
      {activeTab === 'all_transactions' && (
        <div className="db-spend-floater">
          <button
            className="db-spend-floater-pill"
            onClick={() => setShowSpendBreakdown(true)}
            title="Show how the Spending number was calculated"
          >
            <span className="db-sf-desktop-text">🧮 How is {sym}{formatAmount(filteredSummary.debits)} added up?</span>
            <span className="db-sf-mobile-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="20" x2="12" y2="10" />
                <line x1="18" y1="20" x2="18" y2="4" />
                <line x1="6" y1="20" x2="6" y2="16" />
              </svg>
            </span>
          </button>
        </div>
      )}

      {/* Full breakdown modal — opens from the floater pill. Each bucket
          is a clickable card that filters the list to just that bucket
          on tap. The "Counted" / "Not counted" sides sit side-by-side on
          desktop, stack on mobile. */}
      {showSpendBreakdown && (() => {
        const SPEND_KEYS = ['direct', 'card_swipe', 'bill_load', 'scheduled']
          .filter(k => {
            if (k === 'card_swipe') return countingMode === 'items';
            if (k === 'bill_load') return countingMode === 'bills';
            return true;
          });
        const EXCLUDED_KEYS = ['income', 'pure_transfer', 'swipe_skip', 'transfer_other']
          .filter(k => {
            if (k === 'swipe_skip') return countingMode === 'bills';
            return true;
          });

        const renderBucket = (k) => {
          const b = spendingBreakdown[k];
          if (!b || b.count === 0) return null;
          const isActive = bucketFilter === k;
          const isSpend = b.side === 'spend';
          return (
            <button
              key={k}
              className={`db-sb-bucket ${isSpend ? 'spend' : 'excluded'} ${isActive ? 'active' : ''}`}
              onClick={() => {
                setBucketFilter(isActive ? null : k);
                setShowSpendBreakdown(false);
                // If we applied a filter, jump to the list view to show results
                if (!isActive) {
                  setActiveTab('all_transactions');
                }
                // Pull the user's eye to the now-filtered list.
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              title={isActive ? 'Click to remove this filter' : `Show only ${b.label}`}
            >
              <span className="db-sb-bucket-icon">{b.icon}</span>
              <div className="db-sb-bucket-body">
                <span className="db-sb-bucket-lbl">{b.label}</span>
                <span className="db-sb-bucket-hint">{b.hint}</span>
                <span className="db-sb-bucket-count">{b.count} txn{b.count === 1 ? '' : 's'}</span>
              </div>
              <div className="db-sb-bucket-amt-wrap">
                <span className={`db-sb-bucket-amt ${isSpend ? 'spend' : 'excluded'}`}>
                  {isSpend ? '+' : ''}{sym}{formatAmount(b.sum)}
                </span>
                {isActive ? (
                  <span className="db-sb-bucket-action active">✓ Active filter</span>
                ) : (
                  <span className="db-sb-bucket-action">Tap to filter →</span>
                )}
              </div>
            </button>
          );
        };

        const spendCards = SPEND_KEYS.map(renderBucket).filter(Boolean);
        const excludedCards = EXCLUDED_KEYS.map(renderBucket).filter(Boolean);

        return (
          <div className="db-sb-overlay" onClick={() => setShowSpendBreakdown(false)}>
            <div className="db-sb-modal" onClick={e => e.stopPropagation()}>
              <div className="db-sb-mobile-handle" />
              <div className="db-sb-hd">
                <div className="db-sb-title">
                  <span className="db-sb-icon">🧮</span>
                  <div>
                    <div className="db-sb-title-main">Spending breakdown</div>
                    <div className="db-sb-title-sub">Tap any bucket to filter the list</div>
                  </div>
                </div>
                <button
                  className="db-sb-close"
                  onClick={() => setShowSpendBreakdown(false)}
                  aria-label="Close"
                >✕</button>
              </div>

              <div className="db-sb-mode-row">
                <span className="db-sb-mode-lbl">Counting mode:</span>
                <button
                  className={`db-sb-mode-pill ${countingMode}`}
                  onClick={() => setCountingMode(m => m === 'items' ? 'bills' : 'items')}
                >
                  {countingMode === 'items' ? '🛒 Items' : '💳 Bills'}
                </button>
                <span className="db-sb-mode-explain">
                  {countingMode === 'items'
                    ? 'Counting each individual purchase. CC bills & wallet loads are skipped.'
                    : 'Counting CC bill payments & wallet loads. Individual swipes/taps are skipped.'}
                </span>
              </div>

              <div className="db-sb-totals">
                <div className="db-sb-total spend">
                  <span className="db-sb-total-lbl">Spending</span>
                  <span className="db-sb-total-val">{sym}{formatAmount(filteredSummary.debits)}</span>
                </div>
                <div className="db-sb-total income">
                  <span className="db-sb-total-lbl">Income</span>
                  <span className="db-sb-total-val">{sym}{formatAmount(filteredSummary.credits)}</span>
                </div>
              </div>

              <div className="db-sb-grid">
                {/* Counted side */}
                <section className="db-sb-col">
                  <div className="db-sb-col-hd green">
                    <span className="db-sb-col-dot" />
                    Counted in Spending
                  </div>
                  {spendCards.length > 0 ? spendCards : (
                    <div className="db-sb-col-empty">No spending in this view.</div>
                  )}
                </section>

                {/* Not counted side */}
                <section className="db-sb-col">
                  <div className="db-sb-col-hd red">
                    <span className="db-sb-col-dot" />
                    Not counted
                  </div>
                  {excludedCards.length > 0 ? excludedCards : (
                    <div className="db-sb-col-empty">Nothing excluded.</div>
                  )}
                </section>
              </div>

              {bucketFilter && (
                <div className="db-sb-active-banner">
                  <span>Filtering list to <b>{spendingBreakdown[bucketFilter]?.label}</b></span>
                  <button onClick={() => { setBucketFilter(null); }}>Clear</button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      <FloatingNav
        activeTab={activeTab}
        setActiveTab={(tab) => { setActiveTab(tab); setMobileDashMode(null); }}
      />
</div>
  );
};

export default Dashboard;
